/**
 * AchievementPanel — Full-screen panel listing all achievements.
 *
 * Organized into 3 columns:
 *   Left:   Combat achievements
 *   Middle: Exploration achievements
 *   Right:  Character, Social, and Misc achievements
 *
 * Each card shows: icon, name, description, progress bar (if quantitative),
 * and a lock icon when not yet unlocked. Secret achievements are hidden
 * until unlocked.
 *
 * Accessible via:
 *   - "Achievements" button on the main menu
 *   - window.__achievementPanel.show()
 *   - window.__debug.openAchievements()
 */

import { UIPanel } from './UIPanel.js';
import DebugLog from '../debug/DebugLog.js';

const STYLE_ID = 'spellroads-achievement-panel-style';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* ── Achievement Panel ────────────────────────────────────────── */
    #panel-achievements {
      position: fixed;
      inset: 0;
      z-index: 300;
      display: none;
      flex-direction: column;
      background: rgba(6, 4, 3, 0.96);
      color: #d4c090;
      font-family: "Segoe UI", system-ui, sans-serif;
      pointer-events: none;
    }

    #panel-achievements.visible {
      display: flex;
      pointer-events: all;
    }

    /* ── Header ──────────────────────────────────────────────────── */
    .ach-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: clamp(12px, 2vh, 20px) clamp(16px, 3vw, 32px);
      background: rgba(0, 0, 0, 0.5);
      border-bottom: 1px solid rgba(200, 160, 32, 0.3);
      flex-shrink: 0;
    }

    .ach-header-left {
      display: flex;
      align-items: baseline;
      gap: 16px;
    }

    .ach-title {
      font-size: clamp(1rem, 2.5vw, 1.4rem);
      font-weight: 700;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: #c8a020;
      margin: 0;
    }

    .ach-summary {
      font-size: clamp(0.72rem, 1.4vw, 0.85rem);
      color: #7a6a4a;
      letter-spacing: 0.06em;
    }

    .ach-close-btn {
      background: none;
      border: 1px solid rgba(200, 160, 32, 0.25);
      border-radius: 3px;
      color: #a09070;
      font-size: 1.2rem;
      cursor: pointer;
      min-width: 44px;
      min-height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s;
    }

    .ach-close-btn:hover,
    .ach-close-btn:focus {
      border-color: rgba(200, 160, 32, 0.65);
      color: #c8a020;
      background: rgba(200, 160, 32, 0.08);
      outline: none;
    }

    /* ── Body / Columns ──────────────────────────────────────────── */
    .ach-body {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: clamp(12px, 2vw, 20px);
      padding: clamp(12px, 2vh, 20px) clamp(16px, 3vw, 32px);
      overflow-y: auto;
      flex: 1;
    }

    @media (max-width: 767px) {
      .ach-body {
        grid-template-columns: 1fr;
      }
    }

    @media (min-width: 768px) and (max-width: 1199px) {
      .ach-body {
        grid-template-columns: 1fr 1fr;
      }
    }

    /* ── Column ──────────────────────────────────────────────────── */
    .ach-column {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .ach-column-title {
      font-size: clamp(0.7rem, 1.4vw, 0.82rem);
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: #c8a020;
      border-bottom: 1px solid rgba(200, 160, 32, 0.25);
      padding-bottom: 6px;
      margin-bottom: 4px;
    }

    /* ── Achievement Card ────────────────────────────────────────── */
    .ach-card {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 10px 12px;
      background: rgba(200, 160, 32, 0.04);
      border: 1px solid rgba(200, 160, 32, 0.14);
      border-radius: 4px;
      transition: border-color 0.15s;
    }

    .ach-card.unlocked {
      background: rgba(200, 160, 32, 0.08);
      border-color: rgba(200, 160, 32, 0.35);
    }

    .ach-card.locked {
      opacity: 0.55;
    }

    .ach-card-icon {
      font-size: clamp(1.2rem, 2.5vw, 1.5rem);
      flex-shrink: 0;
      line-height: 1;
      margin-top: 2px;
    }

    .ach-card.locked .ach-card-icon {
      filter: grayscale(1);
      opacity: 0.4;
    }

    .ach-card-body {
      flex: 1;
      min-width: 0;
    }

    .ach-card-name {
      font-size: clamp(0.78rem, 1.4vw, 0.88rem);
      font-weight: 600;
      color: #c8a020;
      margin-bottom: 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .ach-card.locked .ach-card-name {
      color: #6a5a3a;
    }

    .ach-card-desc {
      font-size: clamp(0.68rem, 1.2vw, 0.78rem);
      color: #a09070;
      line-height: 1.4;
    }

    .ach-card.locked .ach-card-desc {
      color: #5a4a2a;
    }

    .ach-card-date {
      font-size: 0.65rem;
      color: #5a6a3a;
      margin-top: 4px;
      letter-spacing: 0.04em;
    }

    /* ── Progress bar ────────────────────────────────────────────── */
    .ach-progress-wrap {
      margin-top: 5px;
    }

    .ach-progress-label {
      font-size: 0.65rem;
      color: #7a6a4a;
      margin-bottom: 3px;
    }

    .ach-progress-bar {
      height: 4px;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 2px;
      overflow: hidden;
    }

    .ach-progress-fill {
      height: 100%;
      background: #c8a020;
      border-radius: 2px;
      transition: width 0.4s ease;
    }

    /* ── Lock icon ───────────────────────────────────────────────── */
    .ach-lock-icon {
      flex-shrink: 0;
      font-size: 0.85rem;
      color: #4a3a1a;
      margin-top: 2px;
    }

    /* Secret (hidden until unlocked) */
    .ach-card-secret-placeholder .ach-card-name {
      color: #3a2a0a;
      font-style: italic;
    }
    .ach-card-secret-placeholder .ach-card-desc {
      color: #2a1a0a;
    }
    .ach-card-secret-placeholder .ach-card-icon {
      filter: grayscale(1) brightness(0.3);
    }

    /* ── XP reward badge ─────────────────────────────────────────── */
    .ach-xp-badge {
      font-size: 0.62rem;
      color: #5a8a3a;
      font-weight: 600;
      letter-spacing: 0.04em;
      margin-top: 3px;
    }

    .ach-card.unlocked .ach-xp-badge {
      color: #7ab03a;
    }
  `;

  document.head.appendChild(style);
}

// ─────────────────────────────────────────────────────────────────────────────

export default class AchievementPanel extends UIPanel {
  constructor() {
    super('panel-achievements', { zLayer: 'modal' });
    injectStyles();
    this._buildPanel();
  }

  _buildPanel() {
    this.el.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'ach-header';

    const left = document.createElement('div');
    left.className = 'ach-header-left';

    const title = document.createElement('h2');
    title.className = 'ach-title';
    title.textContent = 'Achievements';
    left.appendChild(title);

    this._summaryEl = document.createElement('span');
    this._summaryEl.className = 'ach-summary';
    this._summaryEl.textContent = '0 / 0 unlocked';
    left.appendChild(this._summaryEl);

    header.appendChild(left);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'ach-close-btn';
    closeBtn.setAttribute('aria-label', 'Close achievements panel');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.hide());
    header.appendChild(closeBtn);

    this.el.appendChild(header);

    // Body (columns populated on show)
    this._bodyEl = document.createElement('div');
    this._bodyEl.className = 'ach-body';
    this.el.appendChild(this._bodyEl);

    DebugLog.info('[ui] AchievementPanel built');
  }

  // Called by UIPanel.show()
  updateContent() {
    this._renderColumns();
  }

  _renderColumns() {
    const as = window.__achievementSystem;
    if (!as) {
      this._bodyEl.innerHTML = '<div style="color:#5a4a2a;padding:24px;">Achievement system not initialized.</div>';
      return;
    }

    const all = as.getAll();
    const summary = as.getSummary();
    this._summaryEl.textContent = `${summary.unlocked} / ${summary.total} unlocked (${summary.percentage}%)`;

    // Group by column
    const columns = {
      'Combat':       all.filter(a => a.category === 'combat'),
      'Exploration':  all.filter(a => a.category === 'exploration'),
      'Other':        all.filter(a => ['character', 'social', 'misc'].includes(a.category)),
    };

    this._bodyEl.innerHTML = '';

    for (const [colLabel, achievements] of Object.entries(columns)) {
      const col = document.createElement('div');
      col.className = 'ach-column';

      const colTitle = document.createElement('div');
      colTitle.className = 'ach-column-title';
      colTitle.textContent = colLabel;
      col.appendChild(colTitle);

      for (const ach of achievements) {
        const isUnlocked = ach.progress.unlocked;

        // Hide secret achievements that are not yet unlocked
        if (ach.secret && !isUnlocked) {
          col.appendChild(this._buildSecretCard());
          continue;
        }

        col.appendChild(this._buildCard(ach, isUnlocked));
      }

      this._bodyEl.appendChild(col);
    }
  }

  _buildCard(ach, isUnlocked) {
    const card = document.createElement('div');
    card.className = `ach-card ${isUnlocked ? 'unlocked' : 'locked'}`;
    card.setAttribute('data-ach-id', ach.id);
    card.setAttribute('role', 'listitem');

    // Icon
    const iconEl = document.createElement('div');
    iconEl.className = 'ach-card-icon';
    iconEl.textContent = ach.icon ?? '🏆';
    card.appendChild(iconEl);

    // Body
    const body = document.createElement('div');
    body.className = 'ach-card-body';

    const nameEl = document.createElement('div');
    nameEl.className = 'ach-card-name';
    nameEl.textContent = ach.name;
    body.appendChild(nameEl);

    const descEl = document.createElement('div');
    descEl.className = 'ach-card-desc';
    descEl.textContent = ach.description;
    body.appendChild(descEl);

    // Progress bar (only for threshold > 1 and not yet unlocked)
    const total = ach.threshold ?? 1;
    if (total > 1 && !isUnlocked) {
      const count = ach.progress.count ?? 0;
      const pct   = Math.min(100, Math.round((count / total) * 100));
      const wrap  = document.createElement('div');
      wrap.className = 'ach-progress-wrap';

      const lbl = document.createElement('div');
      lbl.className = 'ach-progress-label';
      lbl.textContent = `${count} / ${total}`;
      wrap.appendChild(lbl);

      const bar = document.createElement('div');
      bar.className = 'ach-progress-bar';
      const fill = document.createElement('div');
      fill.className = 'ach-progress-fill';
      fill.style.width = `${pct}%`;
      bar.appendChild(fill);
      wrap.appendChild(bar);

      body.appendChild(wrap);
    }

    // Unlock date
    if (isUnlocked && ach.progress.unlockedAt) {
      const dateEl = document.createElement('div');
      dateEl.className = 'ach-card-date';
      dateEl.textContent = `Unlocked ${new Date(ach.progress.unlockedAt).toLocaleDateString()}`;
      body.appendChild(dateEl);
    }

    // XP reward
    if (ach.xpReward) {
      const xpEl = document.createElement('div');
      xpEl.className = 'ach-xp-badge';
      xpEl.textContent = `+${ach.xpReward} XP`;
      body.appendChild(xpEl);
    }

    card.appendChild(body);

    // Lock icon for locked achievements
    if (!isUnlocked) {
      const lockEl = document.createElement('div');
      lockEl.className = 'ach-lock-icon';
      lockEl.setAttribute('aria-label', 'Locked');
      lockEl.textContent = '🔒';
      card.appendChild(lockEl);
    }

    return card;
  }

  _buildSecretCard() {
    const card = document.createElement('div');
    card.className = 'ach-card locked ach-card-secret-placeholder';

    const iconEl = document.createElement('div');
    iconEl.className = 'ach-card-icon';
    iconEl.textContent = '❓';
    card.appendChild(iconEl);

    const body = document.createElement('div');
    body.className = 'ach-card-body';

    const nameEl = document.createElement('div');
    nameEl.className = 'ach-card-name';
    nameEl.textContent = '???';
    body.appendChild(nameEl);

    const descEl = document.createElement('div');
    descEl.className = 'ach-card-desc';
    descEl.textContent = 'Hidden achievement. Keep exploring to discover it.';
    body.appendChild(descEl);

    card.appendChild(body);

    const lockEl = document.createElement('div');
    lockEl.className = 'ach-lock-icon';
    lockEl.textContent = '🔒';
    card.appendChild(lockEl);

    return card;
  }
}
